import { useUserSettings } from '@hooks/settings'
import api, { clearStickyPages } from '@utils/api'
import type { AxiosResponse } from 'axios'
import React, { createContext, type Dispatch, type SetStateAction, useContext, useState } from 'react'
import ReactGA from 'react-ga'
import { useLocation, useNavigate } from 'react-router-dom'

type User = Gravitate.UserResponse
type Auth = Gravitate.AuthorizationResponse
export type SecurityContextType = {
  scopes: Partial<Auth>
  setScopes: Dispatch<SetStateAction<Partial<Auth>>>
  getScopes: () => Promise<Partial<Auth>>
  getUser: () => Promise<User>
  user: User
  setUser: Dispatch<SetStateAction<User>>
  handleLogout: () => void
}

export const SecurityContext = createContext({} as SecurityContextType)

export function SecurityContextProvider({ children }) {
  const [scopes, setScopes] = useState<Partial<Auth>>()
  const [user, setUser] = useState()
  const navigate = useNavigate()
  const location = useLocation()
  const { updateSetting } = useUserSettings()

  const getScopes = async () => {
    if (scopes) {
      return scopes
    }
    const resp = (await api.getAuthorization()) as AxiosResponse<Gravitate.AuthorizationResponse>
    if (!resp) {
      api.logout()
      return
    }
    const auth = resp.data

    setScopes(auth)
    await getUser()

    if (resp.data.store_ids?.length === 1) {
      localStorage.setItem(`last_site_monitoring_page`, resp.data.store_ids[0])
      updateSetting('last_site_monitoring_page', resp.data.store_ids[0])
      if (location.pathname.includes('site_monitoring')) {
        navigate(`/site_monitoring/${resp.data.store_ids[0]}`)
      }
    }

    return { ...resp.data, ...auth }
  }
  const getUser = async () => {
    if (user) {
      return user
    }
    const resp = await api.getUser()
    ReactGA.set({ user: JSON.stringify(resp) })
    if (!resp) return api.logout()
    setUser(resp.data)
    return resp.data
  }
  const handleLogout = () => {
    navigate('/login')
    clearStickyPages()
    api.logout()
    setScopes(undefined)
    setUser(undefined)
  }

  return (
    <SecurityContext.Provider
      value={{
        scopes,
        setScopes,
        getScopes,
        getUser,
        user,
        setUser,
        handleLogout,
      }}
    >
      {children}
    </SecurityContext.Provider>
  )
}

export const useSecurityContext = () => {
  const context = useContext(SecurityContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
